import { FC, Fragment, useState } from "react";
import { JoinFull } from "@mui/icons-material";
import { pad, toBytes, toHex } from "viem";

import type { IServerSignature } from "@dzambalaorg/types-blockchain";
import { useAllowance, useMetamask, useServerSignature, useWriteContractWithSimulation } from "@dzambalaorg/react-hooks-eth";
import { convertDatabaseAssetToChainAsset, convertTemplateToTokenTypeAsset, sortArrObj } from "@framework/exchange";
import { ListAction, ListActionVariant } from "@framework/styled";
import { AddressType, IContract, IMerge, TokenType } from "@framework/types";
import ExchangeMergeFacetMergeABI from "@framework/abis/json/ExchangeMergeFacet/merge.json";
import { ZeroAddress } from "@framework/constants";

import { IMergeDto, MergeDialog } from "./dialog";

interface IMergeButtonProps {
  className?: string;
  disabled?: boolean;
  merge: IMerge;
  variant?: ListActionVariant;
}

export const MergeButton: FC<IMergeButtonProps> = props => {
  const { className, disabled, merge, variant = ListActionVariant.button } = props;

  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);

  const { writeContractAsync } = useWriteContractWithSimulation();

  const referrer = ZeroAddress;

  const { execute: metaFnWithAllowance } = useAllowance(
    async (values: IMergeDto, sign: IServerSignature, systemContract: IContract) => {
      const encodedTemplateId = pad(toHex(BigInt(merge.price?.components[0].templateId || 0)), { size: 32 });

      const items = convertDatabaseAssetToChainAsset(merge.item?.components);

      const price = sortArrObj(values.tokenEntities, { sortBy: "tokenId" }).map(el => ({
        tokenId: el.tokenId,
        tokenType: Object.values(TokenType).indexOf(el.template!.contract!.contractType!),
        token: el.template?.contract?.address,
        amount: "1",
      }));

      return writeContractAsync({
        address: systemContract.address as AddressType,
        abi: ExchangeMergeFacetMergeABI,
        functionName: "merge",
        args: [
          {
            externalId: merge.id,
            expiresAt: sign.expiresAt,
            nonce: toBytes(sign.nonce, { size: 32 }),
            extra: encodedTemplateId,
            receiver: merge.merchant!.wallet,
            referrer: ZeroAddress,
          },
          items,
          price,
          sign.signature,
        ],
      });
    },
  );

  const metaFnWithSign = useServerSignature(
    async (values: IMergeDto, sign: IServerSignature, systemContract: IContract) => {
      const price = sortArrObj(values.tokenEntities, { sortBy: "tokenId" }).map(el =>
        convertTemplateToTokenTypeAsset(el.template),
      );

      return metaFnWithAllowance(
        { contract: systemContract.address, assets: price },
        values,
        sign,
        systemContract,
      ) as Promise<void>;
    },
  );

  const { execute: metaFn } = useMetamask((values: IMergeDto) => {
    return metaFnWithSign(
      {
        url: "/recipes/merge/sign",
        method: "POST",
        data: {
          referrer,
          mergeId: merge.id,
          tokenIds: values.tokenIds,
        },
      },
      values,
    ) as Promise<void>;
  });

  const handleMerge = (): void => {
    setIsMergeDialogOpen(true);
  };

  const handleMergeConfirm = async (dto: IMergeDto) => {
    await metaFn(dto);
    setIsMergeDialogOpen(false);
  };

  const handleMergeCancel = () => {
    setIsMergeDialogOpen(false);
  };

  return (
    <Fragment>
      <ListAction
        icon={JoinFull}
        onClick={handleMerge}
        message="form.buttons.merge"
        className={className}
        dataTestId="MergeButton"
        disabled={disabled}
        variant={variant}
      />
      <MergeDialog
        onConfirm={handleMergeConfirm}
        onCancel={handleMergeCancel}
        open={isMergeDialogOpen}
        message="dialogs.merge"
        merge={merge}
        initialValues={{
          tokens: new Array(parseInt(merge.price?.components[0].amount || "1")).fill({ tokenId: 0 }),
          tokenEntities: [],
          tokenIds: [],
        }}
      />
    </Fragment>
  );
};
